import './index.css';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import Column from 'bloko/blocks/column';
import { createContext, useContext, useState, useLayoutEffect } from 'react';
import classNames from 'classnames';
import VSpacing from 'bloko/blocks/vSpacing';
import Text, { TextSize } from 'bloko/blocks/text';
import isValidKey from 'bloko/common/isValidKey';
import useBreakpoint, { Breakpoint } from 'bloko/common/hooks/useBreakpoint';
import Loading, { LoadingScale } from 'bloko/blocks/loading';

var TemplateWidget;
(function (TemplateWidget) {
    TemplateWidget["Picture"] = "PICTURE";
    TemplateWidget["VacancyBody"] = "VACANCY_BODY";
    TemplateWidget["VacancyAdditional"] = "VACANCY_ADDITIONAL";
    TemplateWidget["ExternalWidget"] = "EXTERNAL_WIDGET";
})(TemplateWidget || (TemplateWidget = {}));
var SidebarBehaviorOnSmallScreen;
(function (SidebarBehaviorOnSmallScreen) {
    SidebarBehaviorOnSmallScreen["MoveToContent"] = "MoveToContent";
    SidebarBehaviorOnSmallScreen["Hide"] = "Hide";
})(SidebarBehaviorOnSmallScreen || (SidebarBehaviorOnSmallScreen = {}));

const TemplateContext = createContext({});
const useTemplateContext = () => useContext(TemplateContext);
const useStylingTemplate = () => {
    const { constructor } = useTemplateContext();
    return constructor.styling;
};
const useExternalComponents = () => {
    const { externalComponents } = useTemplateContext();
    return externalComponents;
};

const columnSizes = (isRedesign) => ({
    xs: '4',
    s: '8',
    m: isRedesign ? '12' : '8',
    l: '10',
});
const SidebarWrapper = ({ children }) => (jsx(Column, { xs: "0", s: "0", m: "0", l: "6", children: children }));
const SidebarContentWrapper = ({ children }) => {
    const { isRedesign } = useTemplateContext();
    return (jsx(Column, { ...columnSizes(isRedesign), l: "0", children: children }));
};
const MainContentWrapper = ({ children }) => {
    const { isRedesign } = useTemplateContext();
    return jsx(Column, { ...columnSizes(isRedesign), children: children });
};

var styles$5 = {"vacancy-view-wrapper_padding-top":"vacancy-view-wrapper-module_vacancy-view-wrapper_padding-top__gFyZc","vacancyViewWrapperPaddingTop":"vacancy-view-wrapper-module_vacancy-view-wrapper_padding-top__gFyZc","vacancy-view-wrapper_padding-bottom":"vacancy-view-wrapper-module_vacancy-view-wrapper_padding-bottom__JQ6O3","vacancyViewWrapperPaddingBottom":"vacancy-view-wrapper-module_vacancy-view-wrapper_padding-bottom__JQ6O3"};

const VacancyViewWrapper = ({ children }) => {
    const { hasBackLayer, backgroundColor } = useStylingTemplate();
    const { hasTopPadding } = useTemplateContext();
    const color = backgroundColor?.lightTheme;
    return (jsx("div", { style: {
            backgroundColor: hasBackLayer && color ? color : undefined,
        }, className: classNames(styles$5.vacancyViewWrapperPaddingBottom, {
            [styles$5.vacancyViewWrapperPaddingTop]: hasTopPadding,
        }), children: children }));
};

var styles$4 = {"noprint":"noprint-module_noprint__-TXNX"};

const NoPrint = ({ Element = 'div', children, ...otherProps }) => {
    return (jsx(Element, { ...otherProps, className: styles$4.noprint, children: children }));
};

const Sidebar = ({ Wrapper }) => {
    const { isRedesign } = useTemplateContext();
    const { SidebarComponent } = useExternalComponents();
    if (!SidebarComponent || !isRedesign) {
        return null;
    }
    return (jsx(Wrapper, { children: jsxs(NoPrint, { children: [jsx(SidebarComponent, {}), jsx(VSpacing, { base: 9, xs: 6 })] }) }));
};

const VacancyViewLayout = ({ children, VacancyContainer, Top }) => {
    const { isRedesign, sidebarBehaviorOnSmallScreen } = useTemplateContext();
    const { ContainerWrapper } = useExternalComponents();
    return (jsx(VacancyViewWrapper, { children: jsxs(VacancyContainer, { isRedesign: isRedesign, ContainerWrapper: ContainerWrapper, rightColumn: jsx(Sidebar, { Wrapper: SidebarWrapper }), children: [jsx(Top, {}), sidebarBehaviorOnSmallScreen === SidebarBehaviorOnSmallScreen.MoveToContent && (jsx(Sidebar, { Wrapper: SidebarContentWrapper })), children] }) }));
};

const getWidgetComponent = (config, configToComponentMap) => {
    if (isValidKey(configToComponentMap, config.type)) {
        return configToComponentMap[config.type];
    }
    return null;
};
const MapConfigToWidget = ({ config }) => {
    const { configToComponentMap } = useTemplateContext();
    const WidgetComponent = getWidgetComponent(config, configToComponentMap);
    if (!WidgetComponent) {
        console.error('Error: unknown widget type', config);
        return null;
    }
    return jsx(WidgetComponent, { ...config });
};

const getStringValueInPixels = (value) => (value ? `${value}px` : undefined);

var styles$3 = {"section-wrapper":"section-wrapper-module_section-wrapper__Bk3xg","sectionWrapper":"section-wrapper-module_section-wrapper__Bk3xg"};

const SectionWrapper = ({ children }) => {
    const { hasBackLayer, borderRadius } = useStylingTemplate();
    return (jsx(MainContentWrapper, { children: hasBackLayer ? (jsxs(Fragment, { children: [jsx("div", { className: styles$3.sectionWrapper, style: { borderRadius: hasBackLayer ? getStringValueInPixels(borderRadius?.value) : undefined }, children: children }), jsx(VSpacing, { base: 7, xs: 5 })] })) : (jsx(Fragment, { children: children })) }));
};

const Section = ({ widgets }) => {
    if (!widgets.length) {
        return null;
    }
    return (jsx(SectionWrapper, { children: jsx(Text, { size: TextSize.Medium, children: widgets.map((config, index) => (jsx(MapConfigToWidget, { config: config }, index))) }) }));
};

var styles$2 = {"backplate":"backplate-module_backplate__61jI3","drop_shadow":"backplate-module_drop_shadow__AU2rC","dropShadow":"backplate-module_drop_shadow__AU2rC","flatten":"backplate-module_flatten__oik25","separate":"backplate-module_separate__5uhMb","content-wrapper":"backplate-module_content-wrapper__IKRZX","contentWrapper":"backplate-module_content-wrapper__IKRZX","content-wrapper-flatten-on-xs":"backplate-module_content-wrapper-flatten-on-xs__Jb8w0","contentWrapperFlattenOnXs":"backplate-module_content-wrapper-flatten-on-xs__Jb8w0"};

const Backplate = ({ children, forceStylesBackplate, height, dropShadow, flattenOnXs, separate, }) => {
    const { hasBackLayer, borderRadius } = useStylingTemplate();
    const hasBackPlate = forceStylesBackplate || !!hasBackLayer;
    const isXS = useBreakpoint() === Breakpoint.XS;
    if (!hasBackPlate) {
        return (jsxs(Fragment, { children: [children, jsx(VSpacing, { base: 1 })] }));
    }
    const style = {
        height: getStringValueInPixels(height),
        borderRadius: forceStylesBackplate && !(flattenOnXs && isXS) ? getStringValueInPixels(borderRadius?.value) : undefined,
    };
    return (jsx("div", { style: style, className: classNames(styles$2.backplate, {
            [styles$2.dropShadow]: dropShadow,
            [styles$2.flatten]: flattenOnXs,
            [styles$2.separate]: separate,
        }), children: children }));
};

var styles$1 = {"widget-wrapper-paddings-on-sides":"widget-wrapper-module_widget-wrapper-paddings-on-sides__9-gED","widgetWrapperPaddingsOnSides":"widget-wrapper-module_widget-wrapper-paddings-on-sides__9-gED"};

const PaddingsOnSides = ({ children }) => (jsx("div", { className: styles$1.widgetWrapperPaddingsOnSides, children: children }));
const WidgetWrapper = ({ paddingsOnSides, children, topPadding, buttomPadding = true, }) => {
    const { hasBackLayer } = useStylingTemplate();
    const renderContent = () => (jsxs(Fragment, { children: [(topPadding || (topPadding === undefined && hasBackLayer)) && jsx(VSpacing, { base: 7, xs: 5 }), children, buttomPadding && jsx(VSpacing, { base: 7, xs: 5 })] }));
    if (paddingsOnSides || (topPadding === undefined && hasBackLayer)) {
        return jsxs(PaddingsOnSides, { children: [" ", renderContent()] });
    }
    return jsxs(Fragment, { children: [" ", renderContent()] });
};

const Spoiler = () => {
    const { renderSpoiler } = useExternalComponents();
    if (!renderSpoiler) {
        return null;
    }
    return (jsx(SectionWrapper, { children: jsx(Backplate, { children: jsx(WidgetWrapper, { children: renderSpoiler() }) }) }));
};

const DIVIDER_TYPE = TemplateWidget.VacancyAdditional;
const mapSimpleWidgetConfigs = (configs) => configs.map((config) => ({ ...config, type: TemplateWidget.ExternalWidget }));
const useSection = () => {
    const { constructor, externalComponents } = useTemplateContext();
    const { widgets } = constructor;
    const firstSectionBottomItems = mapSimpleWidgetConfigs(externalComponents.firstSectionBottomItems || []);
    const secondSectionBottomItems = mapSimpleWidgetConfigs(externalComponents.secondSectionBottomItems || []);
    const dividerIndex = widgets.findIndex((widget) => widget.type === DIVIDER_TYPE);
    let firstSection = [];
    let secondSection = [];
    if (dividerIndex >= 0) {
        firstSection = [...widgets.slice(0, dividerIndex), ...firstSectionBottomItems];
        secondSection = [...widgets.slice(dividerIndex + 1), ...secondSectionBottomItems];
    }
    else {
        firstSection = [...widgets, ...firstSectionBottomItems, ...secondSectionBottomItems];
    }
    return {
        firstSection,
        beforeSections: externalComponents.beforeSections,
        betweenSections: externalComponents.betweenSections,
        secondSection,
    };
};

const Content = () => {
    const { firstSection, beforeSections, betweenSections, secondSection } = useSection();
    const { hideContent } = useContext(TemplateContext);
    return (jsxs(Fragment, { children: [jsx(Spoiler, {}), !hideContent ? (jsxs(Fragment, { children: [!!beforeSections && (jsxs(Fragment, { children: [beforeSections(), jsx(VSpacing, { base: 9, xs: 6 })] })), jsx(Section, { widgets: firstSection }), !!betweenSections && (jsxs(Fragment, { children: [betweenSections(), jsx(VSpacing, { base: 7, xs: 5 })] })), jsx(Section, { widgets: secondSection })] })) : null] }));
};

const ContentWrapper = ({ children, flattenOnXs }) => {
    return jsx("div", { className: flattenOnXs ? styles$2.contentWrapperFlattenOnXs : styles$2.contentWrapper, children: children });
};

const Top = () => {
    const { externalComponents: { topContent }, hideTopBackplate, } = useTemplateContext();
    if (!topContent) {
        return null;
    }
    const content = topContent();
    return (jsxs(MainContentWrapper, { children: [hideTopBackplate ? (content) : (jsx(Backplate, { forceStylesBackplate: true, flattenOnXs: true, dropShadow: true, children: jsx(ContentWrapper, { flattenOnXs: true, children: content }) })), jsx(VSpacing, { base: 9, xs: 6 })] }));
};

const ExternalWidget = ({ Element }) => {
    if (!Element) {
        return null;
    }
    return (jsx(Backplate, { children: jsx(WidgetWrapper, { children: Element }) }));
};

var styles = {"picture-widget":"picture-widget-module_picture-widget__--8Lp","pictureWidget":"picture-widget-module_picture-widget__--8Lp","picture-widget-with-height":"picture-widget-module_picture-widget-with-height__emkwR","pictureWidgetWithHeight":"picture-widget-module_picture-widget-with-height__emkwR","image":"picture-widget-module_image__M-FJq","image_loaded":"picture-widget-module_image_loaded__7Gi0p","imageLoaded":"picture-widget-module_image_loaded__7Gi0p","overlay-content":"picture-widget-module_overlay-content__yYWRf","overlayContent":"picture-widget-module_overlay-content__yYWRf","overlay-content_loaded":"picture-widget-module_overlay-content_loaded__62IXY","overlayContentLoaded":"picture-widget-module_overlay-content_loaded__62IXY","loader":"picture-widget-module_loader__REqIo","pulse":"picture-widget-module_pulse__alxHX","loader_hidden":"picture-widget-module_loader_hidden__wXqIJ","loaderHidden":"picture-widget-module_loader_hidden__wXqIJ"};

const PictureWidgetImage = ({ path, children, dataQa }) => {
    const [loaded, setLoaded] = useState(false);
    useLayoutEffect(() => {
        setLoaded(false);
        const tmpImg = new Image();
        tmpImg.onload = () => {
            setLoaded(true);
        };
        tmpImg.src = path;
    }, [path]);
    return (jsxs(Fragment, { children: [jsx("img", { className: classNames(styles.image, {
                    [styles.imageLoaded]: loaded,
                }), src: path, alt: "", "data-qa": dataQa }), children && (jsx("div", { className: classNames(styles.overlayContent, {
                    [styles.overlayContentLoaded]: loaded,
                }), children: children })), jsx("div", { className: classNames(styles.loader, {
                    [styles.loaderHidden]: loaded,
                }), children: jsx(Loading, { scale: LoadingScale.Medium }) })] }));
};

const getHeight = (brk, height, isRedesign = false) => {
    if (!height || !isRedesign) {
        return undefined;
    }
    if ([Breakpoint.XS, Breakpoint.S].includes(brk)) {
        return height.xs;
    }
    if (brk === Breakpoint.M) {
        return height.m;
    }
    return height.default;
};
const PictureWidgetWrapper = ({ height, renderMetaInfo, children, dataQa, }) => {
    const { hasBackLayer } = useStylingTemplate();
    const { isRedesign } = useTemplateContext();
    const brk = useBreakpoint(Breakpoint.L);
    const heightValue = getHeight(brk, height, isRedesign);
    return (jsxs(WidgetWrapper, { buttomPadding: !hasBackLayer, paddingsOnSides: false, topPadding: false, children: [jsx(Backplate, { forceStylesBackplate: !hasBackLayer, height: heightValue, children: jsx("div", { className: classNames(styles.pictureWidget, {
                        [styles.pictureWidgetWithHeight]: !!heightValue,
                    }), "data-qa": dataQa, children: children }) }), !!renderMetaInfo && renderMetaInfo()] }));
};

const PictureWidget = ({ picture, height, pictureStyle }) => {
    if (!picture?.resizedPath) {
        return null;
    }
    const dataQa = `branded-photo-${pictureStyle.toLowerCase()}`;
    return (jsx(PictureWidgetWrapper, { height: height, dataQa: `${dataQa}-container`, children: jsx(PictureWidgetImage, { path: picture?.resizedPath, dataQa: dataQa }) }));
};

const VacancyBodyWidget = () => {
    const vacancyBody = useExternalComponents().vacancyBody;
    if (!vacancyBody) {
        return null;
    }
    return (jsx(Backplate, { children: jsx(WidgetWrapper, { children: vacancyBody() }) }));
};

const defaultConfigToComponentMap = {
    [TemplateWidget.Picture]: PictureWidget,
    [TemplateWidget.VacancyBody]: VacancyBodyWidget,
    [TemplateWidget.ExternalWidget]: ExternalWidget,
};

const VacancyConstructorView = ({ betweenSections, configToComponentMap = defaultConfigToComponentMap, ContainerWrapper, firstSectionBottomItems, hasTopPadding, hideContent, hideTopBackplate, isRedesign, renderSpoiler, secondSectionBottomItems, SidebarComponent, sidebarBehaviorOnSmallScreen = SidebarBehaviorOnSmallScreen.MoveToContent, templateData, topContent, vacancyBody, VacancyContainer, beforeSections, }) => {
    const template = {
        ...templateData,
        hasTopPadding,
        isRedesign,
        hideContent,
        hideTopBackplate,
        sidebarBehaviorOnSmallScreen,
        configToComponentMap,
        externalComponents: {
            vacancyBody,
            firstSectionBottomItems,
            betweenSections,
            secondSectionBottomItems,
            VacancyContainer,
            topContent,
            SidebarComponent,
            ContainerWrapper,
            renderSpoiler,
            beforeSections,
        },
    };
    return (jsx(TemplateContext.Provider, { value: template, children: jsx(VacancyViewLayout, { VacancyContainer: VacancyContainer, Top: Top, children: jsx(Content, {}) }) }));
};

var style$1 = {"first-section-footer-block":"first-section-footer-block-module_first-section-footer-block__yhCXd","firstSectionFooterBlock":"first-section-footer-block-module_first-section-footer-block__yhCXd"};

const FirstSectionFooterBlock = ({ children }) => {
    return jsx("div", { className: style$1.firstSectionFooterBlock, children: children });
};

var style = {"vacancy-body-block":"vacancy-body-block-module_vacancy-body-block__Iv3qP","vacancyBodyBlock":"vacancy-body-block-module_vacancy-body-block__Iv3qP"};

const VacancyBodyBlock = ({ children }) => {
    return jsx("div", { className: style.vacancyBodyBlock, children: children });
};

export { Backplate, ContentWrapper as BackplateContentWrapper, FirstSectionFooterBlock, MainContentWrapper, PictureWidgetImage, PictureWidgetWrapper, SidebarBehaviorOnSmallScreen, SidebarContentWrapper, SidebarWrapper, TemplateWidget, VacancyBodyBlock, VacancyConstructorView, WidgetWrapper, columnSizes, defaultConfigToComponentMap };
//# sourceMappingURL=index.js.map
